import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../Moduels/Context/AuthContext";

import {

  CalendarClock,

  RefreshCw,

  UserCheck,

  CheckCircle2,

  XCircle,

  Plane,

  Sparkles,

  CalendarX,

  Wand2,

  CalendarSearch,

  Users,

  MapPin,

  Briefcase,

  Filter,

} from "lucide-react";

import API from "../../../services/api";
import {
  bulkUploadAttendance,
  fetchAttendance,
  fetchMyOrganisationSummary,
  normalizeAttendanceStatus,
  approveSpecialWorkPremium,
  upsertAttendanceRecord,
} from "../../../Moduels/attendance/attendanceApi";

import { fetchWfHolidays } from "../../../Moduels/attendance/wfApi";

import EmployeeSelect from "../../common/EmployeeSelect";
import { DepartmentSelect, LocationSelect } from "../../common/OrgStructureSelects";

import AttendanceLockPanel from "../../attendance/AttendanceLockPanel";
import IndianMonthCalendar from "../../attendance/IndianMonthCalendar";
import AttendanceChangeModal from "../../attendance/AttendanceChangeModal";
import { compOffGrantMessage, compOffGrantOffer, creditDayLabel } from "../../attendance/compOffGrantPrompt";
import {
  closeAttendanceCorrection,
  fetchMonthLockStatus,
  openAttendanceCorrection,
} from "../../../Moduels/payroll/payrollApi";
import { applyLeaveRequest, fetchLeaveTypes } from "../../../Moduels/leave/leaveApi";
import { fetchCompOffSettings, fetchMyCompOff } from "../../../Moduels/leave/compOffApi";
import { ALL_LEAVE_TYPES } from "../../pages/Leave/leavePackageCatalog";

import { useEmployees } from "../../../hooks/useEmployees";
import {
  filterEmployeesEligibleForPeriod,
  isEmployeeEligibleForPeriod,
  isWorkDateOnOrAfterJoining,
  toDateOnlyString,
  formatEmployeeLabel,
} from "../../common/employeeUtils";
import {
  formatShiftScheduleLabel,
  shiftScheduleFromPolicyResult,
} from "../../../Moduels/attendance/attendanceCalendarUtils";

import {

  attendanceDayHoursFromSettings,

  buildAttendanceDayMap,

  buildCalendarCells,

  buildHolidayDayMap,

  hasPersistedAttendance,

  isInferredAttendanceRow,

  isCalendarWeekOffDay,

  fixedWorkingDaysFromPayroll,

  mergeAttendanceDisplayMap,

  monthRange,

  parseWorkDate,

  summarizeStatusMap,

  weekOffWeekdaysFromAttendance,

  monthlyOffConfigFromAttendance,

} from "../../../Moduels/attendance/attendanceCalendarUtils";

import { formatMonthYearIndian, formatIndianDate, ATTENDANCE_STATUS_CONFIG } from "../../../Moduels/attendance/indianCalendarUtils";

import "../../../styles/IndianCalendar.css";
import GuidedNotice from "../../ui/GuidedNotice";
import NoticeCard from "../../ui/NoticeCard";



const currentMonth = () => {

  const d = new Date();

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

};



const ALL_EMPLOYEES_VALUE = "__all__";

const DEFAULT_LEAVE_TYPES = ALL_LEAVE_TYPES
  .filter((t) => !t.admin_only && t.code !== "LOP" && t.code !== "HD" && t.code !== "CO" && t.code !== "WFH")
  .map((t) => ({ code: t.code, name: t.name }));

const BULK_CHUNK_SIZE = 250;



function shiftMonth(monthKey, delta) {

  const [y, m] = monthKey.split("-").map(Number);

  const d = new Date(y, m - 1 + delta, 1);

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

}

/**
 * Always returns a clean, user-readable string for any error shape:
 * - String → return as-is
 * - Axios error → prefer response.data.detail / response.data.message
 * - HTTP status codes mapped to friendly text
 * - Arrays / objects → JSON.stringify, never raw [object Object]
 */
function formatErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  if (typeof err === "string") return err;

  const status = err?.response?.status;
  const detail = err?.response?.data?.detail ?? err?.response?.data?.message;

  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .filter(Boolean)
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail); } catch { /* ignore */ }
  }

  // Friendly fallbacks based on HTTP status
  if (status === 400) return "Bad request — please check the form values and try again.";
  if (status === 401) return "Session expired. Please sign in again.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "Not found.";
  if (status === 409) return "Conflict — this action collides with existing data.";
  if (status === 422) return "Some fields are invalid. Please review and try again.";
  if (status === 500) return "Server error. Our team has been notified — please retry shortly.";
  if (status >= 500)  return "Service temporarily unavailable. Please retry shortly.";

  if (typeof err.message === "string" && err.message.trim()) return err.message.trim();

  try { return JSON.stringify(err); } catch { return fallback; }
}



export default function DailyAttendance({
  embedded = false,
  mvpLayout = false,
  hideFilters = false,
  controlledMonth,
  controlledEmployeeId,
  onNavigateToApply,
}) {

  const { role } = useContext(AuthContext);
  const isAdmin = String(role || "").toLowerCase() === "admin";

  const [searchParams, setSearchParams] = useSearchParams();

  const employeeId = controlledEmployeeId ?? searchParams.get("employee") ?? "";
  const isAllEmployees = employeeId === ALL_EMPLOYEES_VALUE;
  const month = controlledMonth ?? searchParams.get("month") ?? currentMonth();

  const patchSearchParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const setEmployeeId = (id) => {
    if (controlledEmployeeId !== undefined) return;
    patchSearchParams({ employee: id || "" });
  };
  const setMonth = (valueOrFn) => {
    if (controlledMonth !== undefined) return;
    const nextMonth =
      typeof valueOrFn === "function" ? valueOrFn(month) : valueOrFn;
    patchSearchParams({ month: nextMonth });
  };



  const [organisationId, setOrganisationId] = useState("");

  const [, setOrganisationName] = useState("");

  const { employees } = useEmployees();

  const [loading, setLoading] = useState(false);

  const [assigning, setAssigning] = useState(false);

  const [error, setError] = useState("");

  const [info, setInfo] = useState("");
  // Short warning without setup guidance (e.g. filling a month that has not started).
  const [plainWarning, setPlainWarning] = useState("");

  useEffect(() => {
    setPlainWarning("");
  }, [month]);

  const [attendance, setAttendance] = useState({});

  const [attendanceRowsByDay, setAttendanceRowsByDay] = useState({});

  const [premiumBusyDay, setPremiumBusyDay] = useState(null);

  const [holidays, setHolidays] = useState([]);

  const [savingDay, setSavingDay] = useState(null);

  const [lockStatus, setLockStatus] = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockError, setLockError] = useState("");
  const [weekOffWeekdays, setWeekOffWeekdays] = useState(null);
  const [monthlyOff, setMonthlyOff] = useState(null);
  const [fixedWorkingDays, setFixedWorkingDays] = useState(null);
  const [fullDayHours, setFullDayHours] = useState(8);
  const [halfDayHours, setHalfDayHours] = useState(4);
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [compOffSettings, setCompOffSettings] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState(DEFAULT_LEAVE_TYPES);
  const [compOffInfo, setCompOffInfo] = useState(null);

  const pendingMarksRef = useRef({});
  const loadIdRef = useRef(0);

  const range = useMemo(() => monthRange(month), [month]);

  const eligibleEmployees = useMemo(
    () => (range ? filterEmployeesEligibleForPeriod(employees, range.from_date, range.to_date) : employees),
    [employees, range]
  );

  const filteredEligibleEmployees = useMemo(() => {
    if (!filterDepartment && !filterLocation) return eligibleEmployees;
    return eligibleEmployees.filter((emp) => {
      if (filterDepartment && String(emp.department_id) !== String(filterDepartment)) return false;
      if (filterLocation && String(emp.location_id) !== String(filterLocation)) return false;
      return true;
    });
  }, [eligibleEmployees, filterDepartment, filterLocation]);

  // Manual department / location filters narrow the list. They must not clear a
  // selection that is still loading, and they must not hide the rest of the list
  // just because one employee is open.
  useEffect(() => {
    if (!filterDepartment && !filterLocation) return;
    if (!employeeId || employeeId === ALL_EMPLOYEES_VALUE) return;
    if (!employees.length) return;
    const match = filteredEligibleEmployees.find((e) => String(e.employee_id) === String(employeeId));
    if (!match) setEmployeeId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDepartment, filterLocation, filteredEligibleEmployees, employeeId, employees.length]);

  const attendanceLocked = lockStatus && !lockStatus.can_edit_attendance;
  const canViewCalendar = lockStatus?.can_view_attendance !== false;

  const holidayMap = useMemo(() => buildHolidayDayMap(holidays, month), [holidays, month]);

  const monthMeta = useMemo(() => formatMonthYearIndian(month), [month]);

  const { cells, weekdayHeaders } = useMemo(() => buildCalendarCells(month), [month]);

  const displayAttendance = useMemo(
    () =>
      mergeAttendanceDisplayMap(attendance, cells, holidayMap, {
        weekOffWeekdays: Array.isArray(weekOffWeekdays) ? weekOffWeekdays : undefined,
        monthlyOff,
        monthKey: month,
        fixedWorkingDays,
        rowsByDay: attendanceRowsByDay,
      }),
    [attendance, cells, holidayMap, weekOffWeekdays, monthlyOff, fixedWorkingDays, month, attendanceRowsByDay]
  );

  const monthSummary = useMemo(

    () => (range ? summarizeStatusMap(displayAttendance, range.daysInMonth) : null),

    [displayAttendance, range]

  );



  const selectedEmployee = useMemo(
    () => employees.find((e) => String(e.employee_id) === String(employeeId)),
    [employees, employeeId]
  );


  const selectedEmployeeEligible = useMemo(() => {
    if (!selectedEmployee || !range) return Boolean(selectedEmployee);
    return isEmployeeEligibleForPeriod(selectedEmployee, range.from_date, range.to_date);
  }, [selectedEmployee, range]);

  const resolvedOrganisationId = useMemo(
    () => organisationId || selectedEmployee?.organisation_id || "",
    [organisationId, selectedEmployee]
  );

  const selectedEmployeeLabel = useMemo(() => {
    if (isAllEmployees) {
      const n = eligibleEmployees.length;
      return n ? `All employees (${n})` : "All employees";
    }
    const emp = selectedEmployee;
    if (!emp) return "";
    return (
      [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
      emp.display_name ||
      emp.employee_code ||
      ""
    );
  }, [isAllEmployees, selectedEmployee, eligibleEmployees.length]);

  const holidayCount = Object.keys(holidayMap).length;

  const pendingPremiumDays = useMemo(() => {
    const pending = [];
    for (const [day, row] of Object.entries(attendanceRowsByDay)) {
      const prem = row?.policy_result_json?.special_work_premium;
      if (prem && prem.approved == null && normalizeAttendanceStatus(row.status) === "P") {
        pending.push({ day: Number(day), row, prem });
      }
    }
    return pending.sort((a, b) => a.day - b.day);
  }, [attendanceRowsByDay]);

  const engineShiftSample = useMemo(() => {
    if (isAllEmployees || !employeeId) return null;
    for (const row of Object.values(attendanceRowsByDay)) {
      const sched = shiftScheduleFromPolicyResult(row?.policy_result_json);
      if (sched) return sched;
    }
    return null;
  }, [attendanceRowsByDay, employeeId, isAllEmployees]);

  const defaultHours = (s) => {

    const st = normalizeAttendanceStatus(s || "");

    if (st === "P") return fullDayHours;

    if (st === "HD") return halfDayHours;

    return 0;

  };



  const loadHolidays = async () => {

    if (!range) return;

    try {

      const rows = await fetchWfHolidays({

        from_date: range.from_date,

        to_date: range.to_date,

      });

      setHolidays(Array.isArray(rows) ? rows : []);

    } catch {

      setHolidays([]);

    }

  };

  const loadAttendanceSettings = async () => {
    try {
      const res = await API.get("/organisation/me/hr-settings");
      const att = res.data?.attendance_settings || res.data?.hr_settings?.attendance || {};
      setWeekOffWeekdays(weekOffWeekdaysFromAttendance(att));
      setMonthlyOff(monthlyOffConfigFromAttendance(att));
      setFixedWorkingDays(fixedWorkingDaysFromPayroll(res.data?.payroll_settings || {}));
      const hours = attendanceDayHoursFromSettings(att);
      setFullDayHours(hours.fullDayHours);
      setHalfDayHours(hours.halfDayHours);
    } catch {
      /* keep last known settings so the calendar does not snap to Sat+Sun */
    }
  };

  const loadCompOffSettings = async () => {
    try {
      setCompOffSettings(await fetchCompOffSettings());
    } catch {
      setCompOffSettings(null);
    }
  };

  const loadCompOff = async (targetEmployeeId) => {
    if (!targetEmployeeId) {
      setCompOffInfo(null);
      return;
    }
    try {
      const data = await fetchMyCompOff(targetEmployeeId);
      setCompOffInfo(data);
    } catch {
      setCompOffInfo(null);
    }
  };

  const loadLeaveTypes = async (orgId) => {
    try {
      const types = await fetchLeaveTypes(orgId);
      if (Array.isArray(types) && types.length) {
        setLeaveTypes(
          types
            .filter((t) => (t.code || t.leave_code) && (t.code || t.leave_code) !== "HD" && (t.code || t.leave_code) !== "WFH")
            .map((t) => ({ code: t.code || t.leave_code, name: t.name || t.leave_name || t.code || t.leave_code }))
        );
      }
    } catch {
      /* keep DEFAULT_LEAVE_TYPES */
    }
  };

  const dayLeaveTypeMap = useMemo(() => {
    const map = {};
    for (const [day, row] of Object.entries(attendanceRowsByDay)) {
      const status = normalizeAttendanceStatus(row?.status);
      if (status === "L" || status === "PL" || status === "UL") {
        let code = null;
        // Check remarks for leave:CODE pattern
        if (row?.remarks) {
          const m = String(row.remarks).match(/leave:(\w+)/);
          if (m) code = m[1];
        }
        // Fallback: check policy_result_json from leave engine
        if (!code && row?.policy_result_json?.leave?.leave_type_code) {
          code = row.policy_result_json.leave.leave_type_code;
        }
        if (code) {
          const lt = leaveTypes.find((t) => t.code === code);
          const name = String(code).toUpperCase() === "CO" ? "Comp Off Leave" : (lt?.name || code);
          map[Number(day)] = { code, name };
        }
      }
    }
    return map;
  }, [attendanceRowsByDay, leaveTypes]);

  const compOffAvailable = Number(compOffInfo?.summary?.available || 0);

  const leaveTypesForModal = useMemo(() => {
    const withoutCompOff = leaveTypes.filter((type) => String(type.code || "").toUpperCase() !== "CO");
    const alreadyCompOff = String(editModal?.currentLeaveType || "").toUpperCase() === "CO";
    if (compOffAvailable > 0 || alreadyCompOff) {
      const label = Number.isInteger(compOffAvailable) ? String(compOffAvailable) : String(compOffAvailable);
      withoutCompOff.push({
        code: "CO",
        name: `Comp Off (${label} day available)`,
      });
    }
    return withoutCompOff;
  }, [leaveTypes, compOffAvailable, editModal]);

  const compOffDayMap = useMemo(() => {
    const map = {};
    const mark = (iso, kind) => {
      const value = String(iso || "").slice(0, 10);
      if (!value.startsWith(`${month}-`)) return;
      const day = Number(value.slice(8, 10));
      if (!day) return;
      if (map[day] !== "used") map[day] = kind;
    };
    for (const credit of compOffInfo?.credits || []) {
      if (["reversed", "rejected"].includes(credit.status)) continue;
      mark(credit.attendance_date || credit.earned_date, "earned");
    }
    for (const application of compOffInfo?.applications || []) {
      if (["rejected", "cancelled"].includes(application.status)) continue;
      const start = new Date(`${String(application.start_date).slice(0, 10)}T00:00:00`);
      const end = new Date(`${String(application.end_date).slice(0, 10)}T00:00:00`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
      const cursor = new Date(start);
      while (cursor <= end) {
        const y = cursor.getFullYear();
        const m = String(cursor.getMonth() + 1).padStart(2, "0");
        const d = String(cursor.getDate()).padStart(2, "0");
        mark(`${y}-${m}-${d}`, "used");
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [compOffInfo, month]);

  const editCompOffOffer = useMemo(() => {
    if (!editModal) return null;
    const holiday = holidayMap[editModal.day];
    const cell = cells.find((c) => !c.empty && c.day === editModal.day);
    const weekOffDay = isCalendarWeekOffDay(editModal.day, cell?.weekdayIndexMon0 ?? null, month, {
      weekOffWeekdays,
      monthlyOff,
      fixedWorkingDays,
    });
    const offer = compOffGrantOffer({
      enabled: Boolean(compOffSettings?.enabled),
      applicableDays: compOffSettings?.applicable_days || "both",
      isHoliday: Boolean(holiday),
      isWeekOff: weekOffDay,
    });
    if (!offer) return null;
    return {
      ...offer,
      holidayName: holiday?.name || "",
      employeeName: formatEmployeeLabel(selectedEmployee) || compOffInfo?.employee?.name || "this employee",
      creditLabel: creditDayLabel(compOffSettings?.credit_value),
      dateLabel: editModal.dateLabel,
    };
  }, [
    editModal,
    holidayMap,
    cells,
    month,
    weekOffWeekdays,
    monthlyOff,
    fixedWorkingDays,
    compOffSettings,
    selectedEmployee,
    compOffInfo,
  ]);



  const loadLockStatus = async () => {
    if (!month) return;
    setLockLoading(true);
    setLockError("");
    try {
      const data = await fetchMonthLockStatus(month);
      setLockStatus(data);
    } catch {
      setLockStatus(null);
    } finally {
      setLockLoading(false);
    }
  };

  const mergePendingMarks = (apiMap) => {
    const pending = pendingMarksRef.current;
    if (!pending || !Object.keys(pending).length) return apiMap;
    return { ...apiMap, ...pending };
  };

  /**
   * All-employees view: a day shows a status once every employee who had joined by that day
   * has saved attendance — the most common saved status (a fully filled day shows "P").
   * Partly marked days stay blank.
   */
  const buildAllEmployeesDayMap = (rows) => {
    const pad = (n) => String(n).padStart(2, "0");
    const byDay = {};
    for (const r of rows || []) {
      if (isInferredAttendanceRow(r)) continue;
      const parts = parseWorkDate(r.work_date);
      if (!parts || parts.monthKey !== month) continue;
      const code = normalizeAttendanceStatus(r.status);
      if (!code) continue;
      if (!byDay[parts.day]) byDay[parts.day] = { employees: new Set(), counts: {} };
      const entry = byDay[parts.day];
      entry.employees.add(String(r.employee_id));
      entry.counts[code] = (entry.counts[code] || 0) + 1;
    }
    const map = {};
    for (const [day, entry] of Object.entries(byDay)) {
      const workDate = `${month}-${pad(Number(day))}`;
      const expected = eligibleEmployees.filter((e) => isWorkDateOnOrAfterJoining(e, workDate));
      const allMarked =
        expected.length > 0 && expected.every((e) => entry.employees.has(String(e.employee_id)));
      if (!allMarked) continue;
      map[day] = Object.entries(entry.counts).sort((a, b) => b[1] - a[1])[0][0];
    }
    return map;
  };

  const load = async () => {

    if (!range || !employeeId) {

      if (!employeeId) setError("Select an employee");

      return;

    }

    const loadId = ++loadIdRef.current;

    setLoading(true);

    setError("");

    try {

      await loadAttendanceSettings();
      await loadCompOffSettings();

      if (isAllEmployees) {
        const allRows = await fetchAttendance({ from_date: range.from_date, to_date: range.to_date });
        if (loadId !== loadIdRef.current) return;
        await loadHolidays();
        if (loadId !== loadIdRef.current) return;
        setAttendance(buildAllEmployeesDayMap(allRows));
        setAttendanceRowsByDay({});
        return;
      }

      const rows = await fetchAttendance({

        employee_id: employeeId,

        from_date: range.from_date,

        to_date: range.to_date,

      });

      if (loadId !== loadIdRef.current) return;

      await loadHolidays();

      if (loadId !== loadIdRef.current) return;

      const dayMap = buildAttendanceDayMap(rows, month);
      const rowMap = {};
      for (const r of rows || []) {
        const parts = parseWorkDate(r.work_date);
        if (!parts || parts.monthKey !== month) continue;
        rowMap[parts.day] = r;
      }
      setAttendance(mergePendingMarks(dayMap));
      setAttendanceRowsByDay(rowMap);

    } catch (e) {

      if (loadId !== loadIdRef.current) return;
      setError(formatErrorMessage(e, "Failed to load attendance"));

    } finally {

      if (loadId === loadIdRef.current) setLoading(false);

    }

  };



  useEffect(() => {

    (async () => {

      try {

        const org = await fetchMyOrganisationSummary();

        if (org?.id) {

          setOrganisationId(org.id);

          setOrganisationName(org.name || org.legal_name || "");

          loadLeaveTypes(org.id);

        }

        await loadAttendanceSettings();
        loadCompOffSettings();

      } catch {

        /* organisation stays empty until loaded */

      }

    })();

  }, []);



  useEffect(() => {
    if (month) loadHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => {
    if (month) loadLockStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);



  useEffect(() => {
    if (!employeeId || isAllEmployees) {
      setCompOffInfo(null);
      return;
    }
    loadCompOff(employeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, isAllEmployees]);

  useEffect(() => {
    pendingMarksRef.current = {};
    if (filtersApplied && employeeId && month) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersApplied, month]);

  useEffect(() => {
    if (!employeeId || isAllEmployees || !employees.length || !range) return;
    if (!selectedEmployeeEligible) {
      setEmployeeId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, isAllEmployees, employees.length, range, selectedEmployeeEligible]);



  const handleOpenCorrection = async () => {
    if (!lockStatus?.pay_period_id) return;
    const reason = window.prompt(
      "Reason for opening attendance correction (optional):",
      "Admin attendance correction"
    );
    if (reason === null) return;
    setLockBusy(true);
    setLockError("");
    setInfo("");
    try {
      const data = await openAttendanceCorrection(lockStatus.pay_period_id, reason);
      setLockStatus(data);
      setInfo("Correction window opened. You can now edit calendar days.");
    } catch (e) {
      setLockError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message || "Failed to open correction");
    } finally {
      setLockBusy(false);
    }
  };

  const handleCloseCorrection = async () => {
    if (!lockStatus?.pay_period_id) return;
    if (
      !window.confirm(
        "Lock attendance again and refresh payroll from the edited days? " +
          "This will recalculate payroll totals for this month."
      )
    ) {
      return;
    }
    const reason = window.prompt(
      "Correction note (optional):",
      "Attendance corrected — payroll refresh"
    );
    if (reason === null) return;
    setLockBusy(true);
    setLockError("");
    setInfo("");
    try {
      const data = await closeAttendanceCorrection(lockStatus.pay_period_id, {
        reason,
        refreshPayroll: true,
      });
      setLockStatus(data);
      setInfo(
        data?.payroll_refresh
          ? "Attendance locked. Payroll refreshed from edited days."
          : "Attendance locked again."
      );
      if (employeeId) await load();
    } catch (e) {
      setLockError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message || "Failed to lock & refresh payroll");
      await loadLockStatus();
    } finally {
      setLockBusy(false);
    }
  };

  const handleApprovePremium = async (day, approved) => {
    const row = attendanceRowsByDay[day];
    if (!row?.attendance_id) return;
    const label = approved ? "approve double pay (2 payable days)" : "reject extra pay";
    if (!window.confirm(`${approved ? "Approve" : "Reject"} special work premium for day ${day}?`)) return;
    setPremiumBusyDay(day);
    setError("");
    try {
      await approveSpecialWorkPremium(row.attendance_id, {
        approved,
        extra_payable_units: approved ? 1 : 0,
        notes: approved ? "HR approved holiday/Sunday work premium" : "HR rejected premium",
      });
      await load();
      setInfo(approved ? `Double pay approved for day ${day}.` : `Premium rejected for day ${day}.`);
    } catch (e) {
      setError(formatErrorMessage(e, `Failed to ${label}`));
    } finally {
      setPremiumBusyDay(null);
    }
  };

  const handleDayClick = (day) => {
    if (!range || !resolvedOrganisationId || !employeeId || isAllEmployees) {
      if (isAllEmployees) {
        setError("Pick one employee to mark individual days. Use Fill working days to apply Present for everyone.");
      } else if (!resolvedOrganisationId) {
        setError("Organisation is still loading. Please wait and try again.");
      }
      return;
    }

    if (attendanceLocked) {
      setError(
        formatErrorMessage(
          lockStatus?.message,
          "Attendance is locked for this month. Ask an admin to open the correction window."
        )
      );
      return;
    }

    const pad = (n) => String(n).padStart(2, "0");
    const work_date = `${month}-${pad(day)}`;

    if (selectedEmployee && !isWorkDateOnOrAfterJoining(selectedEmployee, work_date)) {
      const doj = toDateOnlyString(selectedEmployee.date_of_joining);
      setError(`Attendance starts from the joining date${doj ? ` (${doj})` : ""}. Days before joining cannot be marked.`);
      return;
    }

    const current = normalizeAttendanceStatus(displayAttendance[day] || "");

    const cell = cells.find((c) => !c.empty && c.day === day);
    const weekdayIndex = cell?.weekdayIndexMon0 ?? null;
    const weekOffDay = isCalendarWeekOffDay(day, weekdayIndex, month, {
      weekOffWeekdays,
      monthlyOff,
      fixedWorkingDays,
    });
    const holiday = holidayMap[day];
    const editableStatuses = ["P", "A", "HD", "L"];
    const currentLeaveType = dayLeaveTypeMap[day]?.code || "";
    let hint = "";
    if (holiday || current === "H") {
      hint = `This day is a holiday${holiday?.name ? ` (${holiday.name})` : ""}. Choose Present if the employee worked. You will be asked whether to add it as Comp Off.`;
    } else if (current === "WO" || weekOffDay) {
      hint = "This day is a week off. Choose Present if the employee worked. You will be asked whether to add it as Comp Off.";
    }
    setEditModal({
      day,
      currentStatus: editableStatuses.includes(current) ? current : "P",
      currentLeaveType,
      dateLabel: formatIndianDate(work_date, { withWeekday: true }),
      hint,
    });
  };

  const handleSaveAttendance = async (status, leaveTypeCode, compOffDecision) => {
    if (!editModal) return;
    const { day } = editModal;
    const pad = (n) => String(n).padStart(2, "0");
    const work_date = `${month}-${pad(day)}`;
    const current = attendance[day] || "";
    const remarks = status === "L" && leaveTypeCode ? `leave:${leaveTypeCode}` : "calendar-ui";

    if (status === "L" && String(leaveTypeCode).toUpperCase() === "CO") {
      if (!compOffInfo?.leave_type_id) {
        throw new Error("Comp Off is not enabled yet. Turn it on in Attendance settings.");
      }
      if (compOffAvailable <= 0) {
        throw new Error("This employee has no Comp Off balance left.");
      }
      await applyLeaveRequest({
        organisation_id: resolvedOrganisationId,
        employee_id: employeeId,
        leave_type_id: compOffInfo.leave_type_id,
        start_date: work_date,
        end_date: work_date,
        reason: "Comp Off marked from the attendance calendar",
        half_day: false,
      });
      await loadCompOff(employeeId);
    }

    pendingMarksRef.current = { ...pendingMarksRef.current, [day]: status };
    setAttendance((prev) => ({ ...prev, [day]: status }));
    setSavingDay(day);
    setError("");
    setInfo("");

    try {
      const saved = await upsertAttendanceRecord({
        organisation_id: resolvedOrganisationId,
        employee_id: employeeId,
        work_date,
        status,
        work_hours: defaultHours(status),
        remarks,
        comp_off_decision: compOffDecision,
      });

      const pending = { ...pendingMarksRef.current };
      delete pending[day];
      pendingMarksRef.current = pending;
      loadIdRef.current += 1;

      setAttendanceRowsByDay((prev) => ({
        ...prev,
        [day]: {
          ...(prev[day] || {}),
          work_date,
          status,
          remarks,
          employee_id: employeeId,
        },
      }));
      const leaveLabel = status === "L" && leaveTypeCode
        ? ` (${leaveTypeCode})`
        : "";
      const grant = saved?.comp_off?.[0];
      const creditLabel = creditDayLabel(compOffSettings?.credit_value);
      const grantNote = compOffGrantMessage(grant, compOffDecision, creditLabel);
      const granted = ["available", "duplicate", "pending_approval"].includes(grant?.outcome);
      const statusLabel = ATTENDANCE_STATUS_CONFIG[status]?.label || status;
      if (compOffDecision === "allow" && !granted) {
        setInfo(`Day ${day} marked as ${statusLabel}${leaveLabel}.`);
        setError(grantNote || "Comp Off was not added to the balance.");
      } else {
        setError("");
        setInfo(`Day ${day} marked as ${statusLabel}${leaveLabel}.${grantNote ? ` ${grantNote}` : ""}`);
      }
      if (compOffDecision === "allow" && grant?.outcome === "available") {
        const added = Number(compOffSettings?.credit_value || 1);
        setCompOffInfo((prev) => {
          const credits = Array.isArray(prev?.credits) ? prev.credits : [];
          const already = credits.some(
            (credit) => String(credit.attendance_date || credit.earned_date || "").slice(0, 10) === work_date
              && !["reversed", "rejected"].includes(credit.status)
          );
          if (already) return prev;
          const available = Number(prev?.summary?.available || 0) + added;
          return {
            ...(prev || {}),
            summary: { ...(prev?.summary || {}), available: String(available) },
            credits: [
              ...credits,
              {
                status: "available",
                attendance_date: work_date,
                earned_date: work_date,
                quantity: String(added),
              },
            ],
          };
        });
      }
      if (compOffDecision === "allow" && granted) {
        await loadCompOff(employeeId);
      }
    } catch (e) {
      const pending = { ...pendingMarksRef.current };
      delete pending[day];
      pendingMarksRef.current = pending;

      setAttendance((prev) => {
        const copy = { ...prev };
        if (current) copy[day] = current;
        else delete copy[day];
        return copy;
      });
      setError(formatErrorMessage(e, "Failed to save day"));
      throw e;
    } finally {
      setSavingDay(null);
    }
  };



  const assignDays = async () => {
    setPlainWarning("");

    if (!range || !employeeId || !resolvedOrganisationId) {

      setError(

        !resolvedOrganisationId

          ? "Organisation is still loading. Please wait and try again."

          : "Select employee and month first"

      );

      return;

    }

    if (isAllEmployees && !eligibleEmployees.length) {
      setError(
        employees.length
          ? `No employees had joined by ${monthMeta.label}, so attendance cannot be filled for this month.`
          : "No employees found in this organisation."
      );
      return;
    }

    if (attendanceLocked) {
      setError(
        formatErrorMessage(
          lockStatus?.message,
          "Attendance is locked for this month. Ask an admin to open the correction window."
        )
      );
      return;
    }

    const pad = (n) => String(n).padStart(2, "0");
    // Only days up to today are filled — future days are marked when they happen.
    const now = new Date();
    const todayIso = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

    const workingDays = [];
    for (let day = 1; day <= range.daysInMonth; day += 1) {
      if (`${month}-${pad(day)}` > todayIso) break;
      if (holidayMap[day]) continue;
      const cell = cells.find((c) => !c.empty && c.day === day);
      const weekdayIndex = cell?.weekdayIndexMon0 ?? null;
      if (
        isCalendarWeekOffDay(day, weekdayIndex, month, {
          weekOffWeekdays,
          monthlyOff,
          fixedWorkingDays,
        })
      ) continue;
      workingDays.push({
        day,
        work_date: `${month}-${pad(day)}`,
      });
    }

    if (!workingDays.length) {
      setInfo("");
      setError("");
      setPlainWarning(`${monthMeta.label} has not started yet — attendance can only be filled up to today.`);
      return;
    }

    const targetEmployees = isAllEmployees
      ? eligibleEmployees
      : [{ employee_id: employeeId, date_of_joining: selectedEmployee?.date_of_joining }];

    if (!targetEmployees.length) {
      setError(
        isAllEmployees
          ? employees.length
            ? `No employees had joined by ${monthMeta.label}, so attendance cannot be filled for this month.`
            : "No employees found in this organisation."
          : "Select employee and month first"
      );
      return;
    }

    if (isAllEmployees) {
      if (
        !window.confirm(
          `Mark unmarked working days up to today as Present for all ${targetEmployees.length} employee(s) in ${monthMeta.label}? Days that already have saved attendance will not be changed.`
        )
      ) {
        return;
      }
      setAssigning(true);
      setError("");
      setInfo("");
    }

    const persistedKeys = new Set();
    if (isAllEmployees) {
      try {
        const rows = await fetchAttendance({
          from_date: range.from_date,
          to_date: range.to_date,
        });
        for (const r of rows || []) {
          if (isInferredAttendanceRow(r)) continue;
          const parts = parseWorkDate(r.work_date);
          if (!parts || parts.monthKey !== month) continue;
          persistedKeys.add(`${String(r.employee_id)}:${parts.day}`);
        }
      } catch (e) {
        setAssigning(false);
        setError(formatErrorMessage(e, "Failed to load existing attendance before filling all employees"));
        return;
      }
    }

    const records = [];
    for (const emp of targetEmployees) {
      const eid = emp.employee_id;
      for (const wd of workingDays) {
        if (!isWorkDateOnOrAfterJoining(emp, wd.work_date)) continue;
        if (isAllEmployees) {
          if (persistedKeys.has(`${String(eid)}:${wd.day}`)) continue;
        } else if (hasPersistedAttendance(wd.day, attendanceRowsByDay)) {
          continue;
        }
        records.push({
          employee_id: eid,
          work_date: wd.work_date,
          status: "P",
          work_hours: fullDayHours,
          remarks: "calendar-assign-days",
        });
      }
    }

    if (!records.length) {
      if (isAllEmployees) setAssigning(false);
      setInfo(
        isAllEmployees
          ? "All employees already have saved attendance on working days up to today."
          : "All working days up to today already have saved attendance. Tap any day to change its status."
      );
      setError("");
      return;
    }

    if (
      !isAllEmployees &&
      !window.confirm(
        `Mark ${records.length} unmarked working day(s) up to today as Present? You can still change any day afterwards.`
      )
    ) {
      return;
    }

    if (!isAllEmployees) {
      setAssigning(true);
      setError("");
      setInfo("");
    }

    try {

      let saved = 0;
      let lastPartial = null;
      for (let i = 0; i < records.length; i += BULK_CHUNK_SIZE) {
        const chunk = records.slice(i, i + BULK_CHUNK_SIZE);
        try {
          const result = await bulkUploadAttendance({
            organisation_id: resolvedOrganisationId,
            upsert: true,
            records: chunk,
          });
          saved += (result?.created || 0) + (result?.updated || 0);
        } catch (chunkErr) {
          if (chunkErr.partial) {
            saved += (chunkErr.result?.created || 0) + (chunkErr.result?.updated || 0);
            lastPartial = chunkErr;
            continue;
          }
          throw chunkErr;
        }
      }

      if (!isAllEmployees) {
        const marked = {};
        const markedRows = {};
        for (const rec of records) {
          const day = Number(String(rec.work_date).slice(8, 10));
          if (!day) continue;
          marked[day] = "P";
          markedRows[day] = {
            employee_id: rec.employee_id,
            work_date: rec.work_date,
            status: "present",
            remarks: "calendar-ui",
          };
        }
        setAttendance((prev) => ({ ...prev, ...marked }));
        setAttendanceRowsByDay((prev) => ({ ...prev, ...markedRows }));
      }

      await load();

      if (lastPartial) {
        setInfo(
          isAllEmployees
            ? `Marked Present for ${saved} day(s) across ${targetEmployees.length} employee(s). ${lastPartial.message}`
            : lastPartial.message
        );
        setError("");
      } else {
        setInfo(
          isAllEmployees
            ? `Marked Present for ${saved} day(s) across ${targetEmployees.length} employee(s).`
            : `Marked Present for ${saved} day(s).`
        );
      }

    } catch (e) {

      await load();

      if (e.partial) {

        setInfo(e.message);

        setError("");

      } else {

        setError(formatErrorMessage(e, "Failed to assign days"));

      }

    } finally {

      setAssigning(false);

    }

  };



  const busy = loading || assigning || lockBusy;



  return (

    <div
      className={`${
        mvpLayout
          ? "att-mvp-cal-main"
          : embedded
            ? "att-embedded ind-cal-page p-4 md:p-6"
            : "ind-cal-page p-4 md:p-8"
      } w-full max-w-full min-w-0`}
    >

      {!hideFilters ? (
      <>
      {/* Title + Filter bar */}
      <div
        className={
          embedded
            ? "mb-5 pb-5 border-b border-slate-100"
            : "relative bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 mb-5 overflow-hidden"
        }
      >
        {!embedded ? (
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 via-sky-500 to-indigo-500" />
        ) : null}

        <div className="relative z-10">
          {!embedded ? (
            <div className="flex items-start gap-3 mb-5">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-md shrink-0 mt-0.5">
                <CalendarClock className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Monthly Attendance</h1>
                <p className="text-slate-500 text-sm mt-1">
                  Select a month and employee to view and mark that employee&apos;s monthly calendar. Choose <b>All employees</b> and Fill working days to apply Present for the whole organisation.
                </p>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                <MapPin size={12} className="text-emerald-600" />
                Work Location
              </label>
              <LocationSelect
                hideSelectedHint
                value={filterLocation}
                onChange={(val) => {
                  setFilterLocation(val || "");
                  setFiltersApplied(false);
                }}
              />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                <Briefcase size={12} className="text-violet-600" />
                Department
              </label>
              <DepartmentSelect
                hideSelectedHint
                value={filterDepartment}
                onChange={(val) => {
                  setFilterDepartment(val || "");
                  setFiltersApplied(false);
                }}
              />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                <Users size={12} className="text-indigo-600" />
                Employee
              </label>
              <EmployeeSelect
                value={employeeId}
                onChange={setEmployeeId}
                required
                hideLabel
                employees={filteredEligibleEmployees}
                extraOptions={
                  filteredEligibleEmployees.length
                    ? [{ value: ALL_EMPLOYEES_VALUE, label: `All employees (${filteredEligibleEmployees.length})` }]
                    : []
                }
                selectClassName="border border-slate-200 rounded-xl p-2.5 w-full text-sm focus:ring-2 focus:ring-teal-500/30"
              />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                <CalendarClock size={12} className="text-sky-600" />
                Month &amp; Year
              </label>
              <div className="flex border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                <select
                  className="flex-1 min-w-0 bg-white text-slate-800 px-2.5 py-2 text-sm font-medium border-none outline-none focus:ring-0 appearance-auto"
                  value={month ? month.split("-")[1] : ""}
                  onChange={(e) => {
                    const yr = month ? month.split("-")[0] : String(new Date().getFullYear());
                    setMonth(`${yr}-${e.target.value}`);
                  }}
                >
                  {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
                  ))}
                </select>
                <div className="w-px bg-slate-200" />
                <select
                  className="w-[5.5rem] bg-white text-slate-800 px-2 py-2 text-sm font-medium border-none outline-none focus:ring-0 appearance-auto"
                  value={month ? month.split("-")[0] : ""}
                  onChange={(e) => {
                    const mo = month ? month.split("-")[1] : "01";
                    setMonth(`${e.target.value}-${mo}`);
                  }}
                >
                  {Array.from({ length: 10 }, (_, i) => {
                    const yr = new Date().getFullYear() - 3 + i;
                    return <option key={yr} value={yr}>{yr}</option>;
                  })}
                </select>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1.5 col-span-2 lg:col-span-1">
              <button
                type="button"
                onClick={() => { setFiltersApplied(true); load(); }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white shadow-md transition-all duration-200 whitespace-nowrap bg-gradient-to-r from-[#0f2d52] to-[#1a437d] hover:shadow-lg hover:-translate-y-0.5"
              >
                <Filter size={14} />
                Apply Filters
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterDepartment("");
                  setFilterLocation("");
                  setEmployeeId("");
                  setFiltersApplied(false);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 transition whitespace-nowrap"
              >
                <RefreshCw size={11} />
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary cards + Employee section — only after Apply Filters */}
      {filtersApplied && employeeId ? (
      <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: "Present", value: monthSummary?.P ?? 0, icon: CheckCircle2, soft: "from-emerald-50 to-green-50", border: "border-emerald-200", iconBg: "from-emerald-400 to-green-500", labelColor: "text-emerald-700" },
          { label: "Absent (LOP)", value: monthSummary?.A ?? 0, icon: XCircle, soft: "from-rose-50 to-red-50", border: "border-rose-200", iconBg: "from-rose-400 to-red-500", labelColor: "text-rose-700" },
          { label: "Leave", value: monthSummary?.L ?? 0, icon: Plane, soft: "from-amber-50 to-orange-50", border: "border-amber-200", iconBg: "from-amber-400 to-orange-500", labelColor: "text-amber-700" },
          { label: "Holidays", value: holidayCount, icon: Sparkles, soft: "from-violet-50 to-fuchsia-50", border: "border-violet-200", iconBg: "from-violet-400 to-fuchsia-500", labelColor: "text-violet-700" },
          { label: "Unmarked", value: monthSummary?.unmarked ?? 0, icon: CalendarX, soft: "from-sky-50 to-cyan-50", border: "border-sky-200", iconBg: "from-sky-400 to-cyan-500", labelColor: "text-sky-700" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className={`group relative px-3 py-3 rounded-xl bg-gradient-to-br ${s.soft} border ${s.border} hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 overflow-hidden`}
            >
              <div className="relative flex items-center justify-between">
                <div>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold ${s.labelColor}`}>{s.label}</p>
                  <p className="text-2xl font-extrabold text-slate-900 mt-0.5">{loading ? "—" : s.value}</p>
                </div>
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${s.iconBg} text-white flex items-center justify-center shadow-sm`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Employee + action buttons */}
      <div
        className={
          embedded
            ? "mb-5 pb-5 border-b border-slate-100"
            : "relative bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5 mb-6 overflow-hidden"
        }
      >
        {!embedded ? (
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500" />
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-slate-700">Employee:</p>

          {selectedEmployeeLabel ? (
            <div className="inline-flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
              {isAllEmployees ? (
                <Users className="w-4 h-4 text-teal-600" />
              ) : (
                <UserCheck className="w-4 h-4 text-teal-600" />
              )}
              <span className="font-semibold">{selectedEmployeeLabel}</span>
            </div>
          ) : (
            <span className="text-sm text-slate-400 italic">No employee selected</span>
          )}

          <div className="ml-auto">
            <button
              type="button"
              onClick={assignDays}
              disabled={busy || !employeeId || !month || attendanceLocked}
              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white shadow-md transition-all duration-200 ${
                busy || !employeeId || !month || attendanceLocked
                  ? "bg-slate-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:shadow-xl hover:-translate-y-0.5"
              }`}
            >
              <Wand2 className={`w-4 h-4 ${assigning ? "animate-pulse" : ""}`} />
              {assigning
                ? "Assigning…"
                : isAllEmployees
                  ? "Fill all employees"
                  : "Mark Working Days as Present"}
            </button>
          </div>
        </div>

        {engineShiftSample ? (
          <p className="text-xs text-slate-600 mt-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <b className="text-slate-800">Resolved shift (from attendance engine):</b>{" "}
            {formatShiftScheduleLabel(engineShiftSample)}
            {engineShiftSample.graceMinutes != null ? ` · grace ${engineShiftSample.graceMinutes}m` : ""}
          </p>
        ) : null}

        {attendanceLocked ? (
          <p className="text-xs text-slate-600 mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <b className="text-amber-800">View-only mode.</b> Editing is blocked while payroll is finalized for this month.
          </p>
        ) : null}

        {error ? (
          <GuidedNotice
            domain="attendance"
            message={formatErrorMessage(error)}
            className="mt-3"
            onDismiss={() => setError("")}
          />
        ) : null}

        {plainWarning ? (
          <div className="mt-3">
            <NoticeCard
              variant="warning"
              title="Attendance action failed"
              message={plainWarning}
              onDismiss={() => setPlainWarning("")}
            />
          </div>
        ) : null}

        {info ? (
          <div className="mt-3 flex items-start gap-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 text-emerald-800 rounded-r-lg px-3 py-2 text-sm shadow-sm animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
            <span className="font-medium">{info}</span>
          </div>
        ) : null}

        {!attendanceLocked && pendingPremiumDays.length > 0 ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">Special work — double pay pending HR approval</p>
            <p className="text-xs mt-1 text-amber-800">
              Present marked on holiday / Sunday / week off. Approve to add 1 extra payable day (2× daily wage) in payroll.
            </p>
            <ul className="mt-2 space-y-2">
              {pendingPremiumDays.map(({ day, prem }) => (
                <li key={day} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    Day {day} · {prem.type || "special"} work
                  </span>
                  <button
                    type="button"
                    disabled={busy || premiumBusyDay === day}
                    onClick={() => handleApprovePremium(day, true)}
                    className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {premiumBusyDay === day ? "…" : "Approve 2× pay"}
                  </button>
                  <button
                    type="button"
                    disabled={busy || premiumBusyDay === day}
                    onClick={() => handleApprovePremium(day, false)}
                    className="px-2.5 py-1 rounded-lg border border-slate-300 text-xs font-semibold hover:bg-white disabled:opacity-50"
                  >
                    Reject
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

      </div>
      </>
      ) : null}
      </>
      ) : null}



      {filtersApplied && employeeId && canViewCalendar ? (
        <IndianMonthCalendar
          monthKey={month}
          cells={cells}
          weekdayHeaders={weekdayHeaders}
          holidayMap={holidayMap}
          dayStatusMap={displayAttendance}
          dayLeaveTypeMap={dayLeaveTypeMap}
          compOffDayMap={compOffDayMap}
          weekOffWeekdays={weekOffWeekdays}
          monthlyOff={monthlyOff}
          fixedWorkingDays={fixedWorkingDays}
          onDayClick={handleDayClick}
          busy={assigning || lockBusy}
          readOnly={attendanceLocked || isAllEmployees}
          savingDay={savingDay}
          onMonthChange={(delta) => setMonth((m) => shiftMonth(m, delta))}
          isDayUnavailable={(day) =>
            Boolean(
              selectedEmployee &&
                !isWorkDateOnOrAfterJoining(
                  selectedEmployee,
                  `${month}-${String(day).padStart(2, "0")}`
                )
            )
          }
        />
      ) : (
        <div className="relative rounded-3xl border-2 border-dashed border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-sky-50/50 to-emerald-50/50 px-6 py-14 text-center overflow-hidden">
          <div className="absolute top-4 right-8 w-24 h-24 rounded-full bg-gradient-to-br from-indigo-300/30 to-purple-300/20 blur-2xl" />
          <div className="absolute bottom-4 left-8 w-32 h-32 rounded-full bg-gradient-to-br from-emerald-300/30 to-teal-300/20 blur-2xl" />
          <div className="relative">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 via-sky-500 to-emerald-500 text-white flex items-center justify-center shadow-lg shadow-indigo-500/30 mb-4 animate-pulse">
              <CalendarSearch className="w-7 h-7" />
            </div>
            <p className="text-base font-bold text-slate-800">
              {employeeId ? "No calendar view available" : "Select an employee for this month"}
            </p>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              {employeeId
                ? "Calendar view is not available for this month."
                : eligibleEmployees.length
                  ? `Pick a month (${monthMeta.label}), then choose an employee to view their monthly attendance calendar.`
                  : employees.length
                    ? `No employees had joined by ${monthMeta.label}. Choose a later month, or add employees whose joining date is on or before this month.`
                    : `Pick a month (${monthMeta.label}), then choose an employee to view their monthly attendance calendar.`}
            </p>
          </div>
        </div>
      )}

      {/* Month locking status — after employee calendar */}
      {!hideFilters ? (
        <div className="mt-6">
          <AttendanceLockPanel
            lockStatus={lockStatus}
            loading={lockLoading}
            busy={lockBusy}
            isAdmin={isAdmin}
            lockError={lockError}
            onOpenCorrection={handleOpenCorrection}
            onCloseCorrection={handleCloseCorrection}
          />
        </div>
      ) : null}

      {editModal && (
        <AttendanceChangeModal
          day={editModal.day}
          dateLabel={editModal.dateLabel}
          currentStatus={editModal.currentStatus}
          leaveTypes={leaveTypesForModal}
          compOffAvailable={compOffAvailable}
          currentLeaveType={editModal.currentLeaveType}
          hint={editModal.hint}
          compOffOffer={editCompOffOffer}
          onSave={handleSaveAttendance}
          onCancel={() => setEditModal(null)}
        />
      )}

    </div>

  );

}


